/**
 * Сборка обучающего сервиса — Mini App (§3.2 ТЗ сервиса, §13.1 ТЗ
 * платформы: «три сборки, один шелл»).
 *
 * Своего входа, своих цветов и своего экрана отказа здесь нет: каркас
 * платформы разбирает `401`/`403`, рисует шапку и показывает единый экран
 * отказа §6.4. Сервис передаёт ему одно — откуда брать «кто я»:
 * `/api/study/me` у своего процесса, потому что подпись `initData`
 * проверяется токеном того бота, из которого приложение открыто, и знает
 * его только `study-api`.
 *
 * **Навигация переделана 12.09.2026 по жалобе владельца** — «расположение
 * меню сверху не очень удобно и красиво», и разделы «выглядят
 * дублирующими друг друга». Было: шесть кнопок в одной строке сверху, на
 * телефоне переносившихся в две. Стало:
 *
 * - **разделы сотрудника — внизу**, панелью на всю ширину: до них
 *   дотягивается большой палец, а у официанта телефон в одной руке;
 * - **четыре раздела, а не шесть**: «Наполнение» и «База знаний»
 *   съехались в один «Редактор» (см. `Editor.tsx`) — они парные изнанки
 *   «Справочника» и «Помощника», и в общем ряду с ними стоять не должны;
 * - **вход в «Редактор» — шестерёнкой в строке названия**, справа: это
 *   работа управляющего, а не сотрудника, и места у официанта она не
 *   занимает;
 * - **строка названия** говорит, где человек находится. Без неё панель
 *   внизу — единственный ориентир, а он подписан мелко.
 *
 * Панель живёт здесь, а не в каркасе платформы, по причине D48: Mini App
 * есть ровно у этого сервиса. Правило §8 требует проверять платформенное
 * изменение на трёх сервисах — здесь проверять нечего, потому что
 * менять платформу незачем.
 */

import { AppShell } from '@shell/AppShell';
import { CopyGuard } from '@shell/CopyGuard';
import { setRestaurantTimeZone } from '@shell/format';
import { useState } from 'react';
import { studyApi } from './api';
import type { StudyMe } from './api';
import { Assistant } from './screens/Assistant';
import { Catalog } from './screens/Catalog';
import { Editor } from './screens/Editor';
import { Learning } from './screens/Learning';
import { MentorHome } from './screens/MentorHome';
import { Sections } from './screens/Sections';

/** Раздел нижней панели: короткая подпись, значок и полное название для
 *  строки сверху. Подпись и название разные намеренно: «Показатели» в
 *  панели и «Мои показатели» на экране — одно не влезает, другое не
 *  объясняет. */
interface NavItem {
  code: string;
  label: string;
  title: string;
  icon: string;
}

function BottomNav({
  items,
  current,
  onChange,
}: {
  items: NavItem[];
  current: string;
  onChange: (code: string) => void;
}) {
  if (items.length < 2) {
    return null;
  }
  return (
    <nav className="appnav" aria-label="Разделы">
      {items.map((item) => (
        <button
          key={item.code}
          type="button"
          className={`appnav__item${current === item.code ? ' appnav__item--on' : ''}`}
          aria-current={current === item.code ? 'page' : undefined}
          onClick={() => onChange(item.code)}
        >
          <span className="appnav__icon" aria-hidden="true">
            {item.icon}
          </span>
          <span className="appnav__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

export function App() {
  const [tab, setTab] = useState('catalog');
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <AppShell title="Тоскана — обучение" fetchMe={studyApi.me}>
      {(me) => {
        const study = me as StudyMe;
        // Часовой пояс ресторана приходит с тем же ответом: у платформы и
        // у интерфейса не должно быть двух представлений о том, который
        // сейчас час в ресторане (§17.5 ТЗ платформы).
        if (study.restaurant_tz) {
          setRestaurantTimeZone(study.restaurant_tz);
        }

        // Разделы собираются из назначенных должности (D51) — тем же
        // списком, что прислал сервер. Своего представления о том, кому
        // что положено, у приложения нет и быть не должно.
        const has = (code: string) => study.sections.some((section) => section.code === code);
        const isAdmin = study.is_host || study.level === 'admin' || study.level === 'host';
        const canEdit = (right: string) => isAdmin || (study.rights ?? []).includes(right);

        const items: NavItem[] = [
          ...(has('dishes')
            ? [{ code: 'catalog', label: 'Справочник', title: 'Справочник блюд', icon: '🍽' }]
            : []),
          ...(has('courses')
            ? [{ code: 'learning', label: 'Обучение', title: 'Обучение', icon: '🎓' }]
            : []),
          ...(has('assistant')
            ? [{ code: 'assistant', label: 'Помощник', title: 'Вопрос по регламентам', icon: '💬' }]
            : []),
          // Наставник (S8, S10) — только тем, кому назначен раздел
          // `mentor`. Стажёру он не назначается никогда (D66): у него нет
          // продаж, из которых строится персональная аналитика. Внутри —
          // вкладки «Показатели» и «Тренировка» (`MentorHome.tsx`).
          ...(has('mentor')
            ? [{ code: 'mentor', label: 'Наставник', title: 'Наставник', icon: '📈' }]
            : []),
        ];

        const rights = {
          dishes: canEdit('dishes:edit'),
          courses: canEdit('courses:edit'),
          knowledge: canEdit('assistant:edit'),
          reports: canEdit('reports:view'),
          // Панель наставника `S7` — именные показатели продаж всех, поэтому
          // уровень, а не право раздела; сводка `S8` — только владельцу.
          manager: isAdmin,
          owner: study.is_host,
        };
        const hasEditor =
          rights.dishes || rights.courses || rights.knowledge || rights.reports || rights.manager;

        // Ни разделов, ни редактора — прежний экран со списком доступного
        // и объяснением, к кому идти (стажёр до назначения должности).
        if (items.length === 0 && !hasEditor) {
          return <Sections me={study} />;
        }

        const current = items.some((item) => item.code === tab)
          ? tab
          : (items[0]?.code ?? 'catalog');
        const screen = items.find((item) => item.code === current);
        const title = editorOpen ? 'Редактор' : (screen?.title ?? 'Обучающий агент');

        return (
          <div className={items.length > 1 ? 'with-nav' : undefined}>
            <CopyGuard allowed={Boolean(study.can_copy)} name={study.full_name ?? null} />
            <div className="screen-bar">
              <h2 className="screen-bar__title">{title}</h2>
              {hasEditor && (
                <button
                  type="button"
                  className={`screen-bar__gear${editorOpen ? ' screen-bar__gear--on' : ''}`}
                  title={editorOpen ? 'Выйти из редактора' : 'Редактор: блюда, курсы, регламенты'}
                  aria-label={editorOpen ? 'Выйти из редактора' : 'Открыть редактор'}
                  onClick={() => setEditorOpen((open) => !open)}
                >
                  {editorOpen ? '✕' : '⚙'}
                </button>
              )}
            </div>

            {editorOpen ? (
              <Editor rights={rights} />
            ) : (
              <>
                {current === 'learning' && <Learning />}
                {/* Вопрос про состав и аллергены помощник не разбирает и
                    уводит в справочник (D55) — кнопка под его ответом
                    переключает раздел, чтобы совет не заканчивался на
                    слове «посмотрите». */}
                {current === 'assistant' && <Assistant onOpenCatalog={() => setTab('catalog')} />}
                {current === 'mentor' && <MentorHome />}
                {current === 'catalog' && <Catalog />}
              </>
            )}

            {!editorOpen && (
              <BottomNav items={items} current={current} onChange={setTab} />
            )}
          </div>
        );
      }}
    </AppShell>
  );
}
